import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Engine',
  'Brakes',
  'Transmission',
  'Electrical',
  'Tires & Suspension',
  'Diesel Specific',
  'General',
  'Pricing',
  'Hours & Location'
];

const KnowledgeBase = () => {
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    category: 'General',
    question: '',
    answer: '',
    keywords: ''
  });

  useEffect(() => {
    fetchKnowledge();
  }, []);

  const fetchKnowledge = async () => {
    try {
      const response = await axios.get(`${API}/knowledge`);
      setKnowledge(response.data);
    } catch (error) {
      console.error('Error fetching knowledge:', error);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      category: formData.category,
      question: formData.question,
      answer: formData.answer,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/knowledge/${editingItem.id}`, data);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/knowledge`, data);
        toast.success('FAQ added');
      }
      
      fetchKnowledge();
      resetForm();
      setIsAddOpen(false);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    
    try {
      await axios.delete(`${API}/knowledge/${id}`);
      toast.success('FAQ deleted');
      fetchKnowledge();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      question: item.question,
      answer: item.answer,
      keywords: item.keywords?.join(', ') || ''
    });
    setIsAddOpen(true);
  };

  const resetForm = () => {
    setFormData({
      category: 'General',
      question: '',
      answer: '',
      keywords: ''
    });
    setEditingItem(null);
  };

  const filteredKnowledge = knowledge.filter(item => {
    const matchesSearch = !searchTerm || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8" data-testid="knowledge-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
            KNOWLEDGE BASE
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage FAQs and repair knowledge for the AI receptionist
          </p>
        </div>
        
        <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary px-6 py-3" data-testid="add-faq-btn">
              <Plus className="w-5 h-5 mr-2" />
              ADD FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-foreground">
                {editingItem ? 'EDIT FAQ' : 'ADD NEW FAQ'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger className="input-industrial" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-foreground hover:bg-[#27272A]">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Question</label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({...formData, question: e.target.value})}
                  placeholder="What should the AI know about?"
                  className="input-industrial"
                  required
                  data-testid="question-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Answer</label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({...formData, answer: e.target.value})}
                  placeholder="How should the AI respond?"
                  className="input-industrial min-h-[120px]"
                  required
                  data-testid="answer-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Keywords (comma-separated)</label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                  placeholder="brake, grinding, noise"
                  className="input-industrial"
                  data-testid="keywords-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <DialogClose asChild>
                  <Button type="button" variant="outline" className="flex-1 border-border">
                    <X className="w-4 h-4 mr-2" />
                    CANCEL
                  </Button>
                </DialogClose>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-faq-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'UPDATE' : 'SAVE'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search FAQs..."
            className="input-industrial pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="input-industrial w-full md:w-[200px]" data-testid="filter-category">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-muted border-border">
            <SelectItem value="all" className="text-foreground hover:bg-[#27272A]">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat} className="text-foreground hover:bg-[#27272A]">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Knowledge List */}
      <Card className="card-modern">
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            {filteredKnowledge.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <p className="mb-4">No FAQs found</p>
                <Button 
                  onClick={() => setIsAddOpen(true)} 
                  className="btn-secondary"
                >
                  Add your first FAQ
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-[#27272A]">
                {filteredKnowledge.map((item, idx) => (
                  <div 
                    key={item.id} 
                    className="p-6 hover:bg-white/[0.02] transition-colors"
                    data-testid={`faq-item-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-mono px-2 py-1 rounded-sm bg-[#FF3B30]/20 text-[#FF3B30]">
                            {item.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          {item.question}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {item.answer}
                        </p>
                        {item.keywords && item.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {item.keywords.map((kw, i) => (
                              <span 
                                key={i} 
                                className="text-xs px-2 py-1 rounded-sm bg-[#27272A] text-muted-foreground"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEdit(item)}
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground hover:text-foreground hover:bg-[#27272A]"
                          data-testid={`edit-faq-${idx}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(item.id)}
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                          data-testid={`delete-faq-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default KnowledgeBase;
